import React, { useMemo } from 'react';
import ProductImage1 from '../assets/product/product1.webp';
import ProductImage3 from '../assets/product/product3.webp';
import OneMonth from '../assets/product/90days.png';
import ThreeMonth from '../assets/product/3m-new.webp';
import SixMonth from '../assets/product/6m-new.webp';
import Ingredients from '../assets/product/tryonce.webp';
import { Icon } from './elements/icon';
import { useTranslation } from 'react-i18next';
import {
  Box1mLine1,
  Box1mLine2,
  Box3mLine1,
  Box6mLine1,
  Box6mLine3,
  MonthlyPlan,
  ThreeMonthPlan,
  StartNowButton,
  PerDay,
  Month,
  TryOnceBox1,
  TryOnceBox2,
  TryOnceBox3,
  Reformulation,
  Free,
  Shipping,
  NutritionistAccess,
  WelcomeKit,
  AndMore,
  YouAre,
  Saving,
  InAverage,
  WhatAreYouGetting,
  Available,
  OrderNow,
  AvailableText,
  EveryThreeMonths,
  TryOnceMobile,
  Save,
  SecurePayment,
  ShippedIn2To3Days,
  Verified,
  Subscription,
  Or,
  PauseAndCancel,
  SinglePurchase,
  ThreeMonthsCourse,
  BestValueMobile,
  LovedByNewCustomer,
  DailyServings,
  FreeGiftsMobile,
} from '../Constants';
import GuaranteeImg from '../assets/Guarantee.webp';
import GiftImg from '../assets/Gift.webp';
import TickImgBlack from '../assets/black-tick.png';
import GuaranteeTick from '../assets/Guarantee.webp';
import InfoCard from './Customer/components/InfoCard';
import PaymentMethods from '../assets/payment-methods.webp';
import ShippingImg from '../assets/shipping-DHL.webp';
import ReviewsImage from '../assets/reviews.webp';

// Utility functions
const DURATION_ORDER = ['3 months', '6 months', '90 days'];

const getPriceByDuration = (duration, totals, multiplier = 1) => {
  const priceMap = {
    '6 months': totals.sixMonths,
    '3 months': totals.threeMonths,
    '90 days': totals.threeMonthsOneOff,
    '1 month': totals.oneMonth,
    '30 days': totals.oneMonth,
  };
  return (parseFloat(priceMap[duration] || 0) * multiplier).toFixed(2);
};

const getPerDayPrice = (price, duration) => {
  const days = duration === '6 months' || duration === '90 days' ? 90 : 30;
  return (price / days).toFixed(2);
};

// Reusable Components
const CustomCheckbox = ({ checked = false, ...props }) => (
  <button
    type="button"
    disabled
    className={`t-w-5 t-h-5 t-rounded-full t-border-2 t-flex t-items-center t-justify-center t-transition-all t-duration-200 t-mt-[3px] ${
      checked ? 't-bg-white t-border-secondary t-p-[3px]' : 't-bg-white t-border-gray-800'
    }`}
    {...props}
  >
    {checked && <div className="t-w-full t-h-full t-bg-secondary t-rounded-full" />}
  </button>
);

const DividerWithText = ({ text }) => (
  <div className="t-flex t-items-center">
    <div className="t-flex-grow t-border-t t-border-gray-400" />
    <span className="t-mx-3 t-text-gray-800 t-font-semibold t-tracking-wide">{text}</span>
    <div className="t-flex-grow t-border-t t-border-gray-400" />
  </div>
);

const ProductHeader = ({ username, bannerImage, productImage, duration }) => (
  <div className="t-flex t-flex-row t-gap-4">
    <div className="t-flex t-flex-col t-items-start t-justify-between t-gap-4">
      <p className="t-m-0 t-font-[700] t-text-2xl">{username}'s mix</p>
      <img src={bannerImage} alt="Plan banner" className={`${duration === '90 days' && 't-h-[73px]'}`} />
    </div>
    <img
      src={productImage}
      alt="Product"
      className={`${
        ['6 months', '3 months', '1 month'].includes(duration)
          ? '!t-h-[155px] !t-w-[155px] t-object-contain'
          : '!t-h-[155px] !t-w-[155px] t-object-contain'
      } t-w-[40%]`}
    />
  </div>
);

const PlanBadge = ({ duration, t }) => {
  if (duration === '6 months') {
    return (
      <p className="!t-text-[10px] !t-m-0 !t-absolute !t-right-3 !-t-top-[10px] t-bg-gray-300 t-text-gray-700 t-px-4 t-z-10">
        {t(BestValueMobile)}
      </p>
    );
  }
  if (duration === '3 months') {
    return (
      <p className="!t-text-[10px] !t-m-0 !t-absolute !t-right-3 !-t-top-[10px] t-bg-primary t-text-gray-700 t-px-4 t-z-10">
        {t(LovedByNewCustomer)}
      </p>
    );
  }
  return null;
};

const PlanDetails = ({ isSubscription, duration, t, perDay }) => {
  if (isSubscription) {
    return (
      <div className="t-relative t-flex t-items-center t-justify-between t-p-2">
        <div className="t-flex t-flex-col t-gap-2 t-text-gray-600">
          <DetailItem
            icon={TickImgBlack}
            text={`${duration === '6 months' ? '3x30' : duration === '90 days' ? '90' : '30'} ${t(
              DailyServings
            )}`}
          />
          <DetailItem icon={GuaranteeTick} text={t(PauseAndCancel)} />
          <DetailItem icon={GiftImg}>
            <span className="t-line-through">10€</span> {t(FreeGiftsMobile)}
          </DetailItem>
        </div>
        <PerDayBadge price={perDay} t={t} />
      </div>
    );
  }

  return (
    <div className="t-relative t-flex t-items-center t-justify-between t-p-2">
      <div className="t-flex t-flex-col t-gap-2 t-text-gray-600">
        <DetailItem icon={TickImgBlack} text={`90 ${t(DailyServings)}`} />
        <DetailItem icon={TickImgBlack} text={t(SinglePurchase)} />
      </div>
      <PerDayBadge price={perDay} t={t} />
    </div>
  );
};

const DetailItem = ({ icon, text, children }) => (
  <div className="t-flex t-items-center t-gap-2">
    <img src={icon} alt="" className="t-h-4 t-w-4" />
    <p className="t-m-0 t-text-sm">{children || text}</p>
  </div>
);

const PerDayBadge = ({ price, t }) => (
  <p className="t-absolute t-right-2 t-top-2 !t-m-0 !t-px-2 !t-text-xs !t-rounded-lg !t-border t-border-gray-400">
    {price}€ {t(PerDay)}
  </p>
);

const SubscriptionCard = ({ item, isActive, totals, onSelectPlan, t }) => {
  const isSubscription = ['6 months', '3 months'].includes(item.duration);
  const monthlyPrice = getPriceByDuration(item.duration, totals);
  const totalPrice = item.duration === '3 months' ? Math.round(monthlyPrice) : Math.round(monthlyPrice * 3);
  const strikethroughPrice =
    item.duration === '3 months'
      ? Math.round(parseFloat(totals.oneMonth || 0))
      : Math.round(parseFloat(totals.oneMonth || 0) * 3);
  const perDay = getPerDayPrice(totalPrice, item.duration);

  const getPlanTitle = () => {
    if (item.duration === '30 days') return t(TryOnceMobile);
    if (item.duration === '90 days') return t(ThreeMonthsCourse);
    if (item.duration === '3 months') return t(MonthlyPlan);
    return t(ThreeMonthPlan);
  };

  return (
    <div
      className={`t-rounded-lg t-border-2 t-p-[2px] t-mb-4 ${
        isActive ? 't-border-secondary' : 't-border-gray-300'
      }`}
      onClick={() => onSelectPlan(item)}
      style={{ fontFamily: 'Roboto' }}
    >
      <div
        className={`!t-relative t-flex t-items-center t-justify-between t-p-2 t-rounded-lg ${
          isActive ? 't-bg-secondary t-text-white' : 't-bg-gray-100'
        }`}
      >
        <div className="t-flex t-items-start t-gap-1">
          <CustomCheckbox checked={isActive} />
          <div className="t-flex t-flex-col">
            <h1 className={`!t-m-0 !t-text-lg ${isActive ? '!t-font-[900]' : '!t-font-normal'}`}>
              {getPlanTitle()}
            </h1>
            {isSubscription && (
              <span className="t-bg-primary t-text-secondary !t-px-2 t-rounded-full t-w-fit">
                {t(Save)} {item.duration === '3 months' ? '10%' : '30%'}
              </span>
            )}
          </div>
        </div>
        <div className="t-flex t-flex-col t-items-end t-justify-end">
          <p
            className={`t-m-0 t-text-xl t-font-[700] t-text-center ${
              isActive ? 't-text-white' : 't-text-secondary'
            }`}
          >
            <span className={`t-line-through t-mr-2 ${isActive ? 't-text-gray-400' : 't-text-gray-400'}`}>
              {strikethroughPrice}€
            </span>
            {totalPrice}€
          </p>
          <p className={`!t-m-0 ${!isActive && '!t-text-secondary'}`}>
            {Math.round(monthlyPrice)}€ {'/' + t(Month)}
          </p>
        </div>
        <PlanBadge duration={item.duration} t={t} />
      </div>
      {isActive && (
        <PlanDetails isSubscription={isSubscription} duration={item.duration} t={t} perDay={perDay} />
      )}
    </div>
  );
};

const BenefitsList = ({ duration, focusArea, totalIngredients, t }) => {
  const twoFocusArea = focusArea?.slice(0, 2).map((item) => item?.answer);

  const tickList = useMemo(() => {
    const list = [
      { durations: ['6 months'], text: t(Box6mLine1)?.replace('{0}', totalIngredients) },
      { durations: ['3 months'], text: t(Box3mLine1)?.replace('{0}', totalIngredients) },
      { durations: ['1 month'], text: t(Box1mLine1) },
      { durations: ['90 days'], text: t(TryOnceBox1)?.replace('{0}', totalIngredients) },
    ];

    if (twoFocusArea) {
      list.push({
        durations: ['6 months', '3 months'],
        text: t(TryOnceBox3) + twoFocusArea.map((item) => ' ' + item).join('') + ' ' + t(AndMore),
      });
    }

    list.push(
      { durations: ['1 month'], text: t(Box1mLine2) },
      { durations: ['90 days'], text: t(TryOnceBox2) },
      { durations: ['6 months', '3 months'], text: t(Box6mLine3) }
    );

    if (twoFocusArea) {
      list.push({
        durations: ['90 days'],
        text: t(TryOnceBox3) + twoFocusArea.map((item) => ' ' + item) + ' ' + t(AndMore),
      });
    }

    return list;
  }, [twoFocusArea, totalIngredients, t]);

  const freeItems = [
    { old: '6€', label: Shipping },
    { old: '20€', label: NutritionistAccess },
    { old: '10€', label: WelcomeKit },
  ];

  return (
    <div className="t-flex t-flex-col !t-gap-2">
      {tickList
        .filter(({ durations }) => durations.includes(duration))
        .map((item, idx) => (
          <div className="t-flex t-items-start t-gap-2" key={idx}>
            <Icon name="tick" size="16" className="t-mt-1" />
            <p className="t-text-sm t-mb-0">{item.text}</p>
          </div>
        ))}

      {duration !== '90 days' &&
        freeItems.map(({ old, label }, idx) => (
          <div className="t-flex t-items-start t-gap-2" key={idx}>
            <img src={GiftImg} alt="gift" className="t-w-4 t-h-4" />
            <p className="t-text-sm t-mb-0">
              <span className="t-line-through" style={{ fontFamily: 'roboto' }}>
                {old}
              </span>{' '}
              <span className="t-text-extrabold" style={{ fontFamily: 'roboto' }}>
                {t(Free)}
              </span>{' '}
              {t(label)}
            </p>
          </div>
        ))}

      <div className="t-flex t-items-start t-gap-2">
        <img src={GuaranteeImg} alt="guarantee" className="t-w-4 t-h-4" />
        <p className="t-text-sm t-mb-0">{t(Reformulation)}</p>
      </div>
    </div>
  );
};

// Main Component
const MobileSubscriptionCardV2 = ({
  username,
  subscribtion = {},
  subs = [],
  onSelectPlan,
  totals = {},
  paymentLink,
  isCheckoutLoading = false,
  onSubmitData,
  error = {},
  showBtn = true,
  boxRef,
  focusArea,
  totalIngredients,
}) => {
  const { t } = useTranslation();
  const { duration = '' } = subscribtion;

  const sortedSubs = useMemo(
    () => subs.sort((a, b) => DURATION_ORDER.indexOf(a.duration) - DURATION_ORDER.indexOf(b.duration)),
    [subs]
  );

  const bannerImages = { '6 months': SixMonth, '3 months': ThreeMonth, '1 month': OneMonth };
  const productImages = { '6 months': ProductImage3, '3 months': ProductImage1, '30 days': Ingredients };

  const bannerImage = bannerImages[duration] || OneMonth;
  const productImage = productImages[duration] || Ingredients;

  const totalPrice = Math.round(
    getPriceByDuration(duration, totals, duration === '6 months' || duration === '90 days' ? 3 : 1)
  );
  const perDayPrice = getPerDayPrice(totalPrice, duration);
  const showStrikeThrough = ['6 months', '3 months', '90 days'].includes(duration);
  const disabledBtn = isCheckoutLoading || !paymentLink || Object.values(error).some(Boolean);

  const getButtonPriceText = () => {
    if (duration === '30 days' || duration === '90 days') return `${totalPrice}€`;
    if (duration === '6 months') return `${totalPrice}€ ${t(EveryThreeMonths)}`;
    return `${totalPrice}€ /${t(Month)}`;
  };

  return (
    <div className="sm:t-hidden t-relative !t-w-full t-bg-white !t-max-w-[500px] t-flex t-flex-col t-py-4 md:t-py-6 t-rounded-lg t-gap-2">
      <ProductHeader
        username={username}
        bannerImage={bannerImage}
        productImage={productImage}
        duration={duration}
      />

      <div className="t-flex t-flex-col t-justify-center !t-items-stretch">
        {sortedSubs.slice(0, 3).map((item) => {
          const isActive = subscribtion?.subId === item?.id;

          return (
            <React.Fragment key={item.id}>
              {item.duration === '3 months' && (
                <h1 className="t-text-base !t-font-[900]" style={{ fontFamily: 'Roboto' }}>
                  {t(Subscription)}
                </h1>
              )}
              {item.duration === '90 days' && (
                <>
                  <DividerWithText text={t(Or)} />
                  <h1 className="t-text-base !t-font-[900]" style={{ fontFamily: 'Roboto' }}>
                    {t(TryOnceMobile)}
                  </h1>
                </>
              )}
              <SubscriptionCard
                item={item}
                isActive={isActive}
                totals={totals}
                onSelectPlan={onSelectPlan}
                t={t}
              />
            </React.Fragment>
          );
        })}
      </div>

      {showBtn && (
        <button
          ref={boxRef}
          className="!t-flex !t-items-center !t-justify-center !t-gap-2 !t-text-sm !t-text-capitalize btn-custom-quaternary !t-py-2 !t-bg-quaternary disabled:!t-bg-quaternary hover:!t-text-white disabled:t-opacity-50 !t-rounded-lg"
          onClick={() => onSubmitData(paymentLink)}
          disabled={disabledBtn}
        >
          {t(StartNowButton)}
          <span className="!t-m-0 t-text-white !t-font-bold">
            {showStrikeThrough && (
              <span className="t-line-through">
                {['6 months', '90 days'].includes(duration)
                  ? Math.round(parseFloat(totals.oneMonth || 0) * 3)
                  : Math.round(parseFloat(totals.oneMonth || 0))}
                €
              </span>
            )}{' '}
            {getButtonPriceText()}
          </span>
        </button>
      )}

      <div className="t-flex t-items-start t-gap-1 t-flex-row">
        <div className="t-flex-none t-w-3 t-h-3 t-bg-green-400 t-mt-[5px] t-rounded-full" />
        <p className="t-text-sm t-m-0 t-text-green-400 !t-font-bold">
          {t(Available)}&nbsp;
          <span className="t-text-sm t-mt-0 t-text-black t-font-extrabold" style={{ fontFamily: 'roboto' }}>
            - {t(OrderNow)}
          </span>
          <span className="t-text-sm t-mt-0 t-text-black">{t(AvailableText)}</span>
        </p>
      </div>

      <div className="t-flex t-items-start t-justify-center t-gap-8 !t-mt-8">
        <InfoCard image={PaymentMethods} desc={t(SecurePayment)} className="md:!t-w-1/2" />
        <InfoCard image={ShippingImg} desc={t(ShippedIn2To3Days)} pClass="md:t-mt-[10px]" />
        <InfoCard image={ReviewsImage} desc={t(Verified)} pClass="md:t-mt-[10px]" />
      </div>

      <p className="t-m-0">
        {t(WhatAreYouGetting)}{' '}
        <span className="t-text-extrabold t-text-secondary" style={{ fontFamily: 'roboto' }}>
          {perDayPrice}€
        </span>{' '}
        {t(PerDay)}:
      </p>

      <BenefitsList duration={duration} focusArea={focusArea} totalIngredients={totalIngredients} t={t} />

      <p className="!t-m-0 t-p-2 t-bg-gray-100 t-rounded-lg t-text-center">
        {t(YouAre)}{' '}
        <span className="t-text-extrabold t-text-secondary" style={{ fontFamily: 'roboto' }}>
          {t(Saving)} 34%
        </span>{' '}
        {t(InAverage)}
      </p>
    </div>
  );
};

export default MobileSubscriptionCardV2;
